import { useCallback, useEffect, useState } from 'react';

import { formatExactDeadline, getBattleCountdown } from '../lib/battle-countdown';
import type { Battle, VoteState } from '../types';
import { SponsoredSlot } from './SponsoredSlot';

export function BattleVotePanel({
  battle,
  authenticated,
  onFinalized,
}: {
  battle: Battle;
  authenticated: boolean;
  onFinalized: (slug: string, winnerSide: Battle['winnerSide']) => void;
}) {
  const [state, setState] = useState<VoteState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const loadState = useCallback(async () => {
    const response = await fetch(`/api/v1/battles/${battle.slug}/vote`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const body = (await response.json()) as VoteState;
    setState(body);
    if (body.battleStatus === 'closed') onFinalized(battle.slug, body.winnerSide);
    return body;
  }, [battle.slug, onFinalized]);

  useEffect(() => {
    let active = true;
    void loadState().then((body) => {
      if (!active && body) setState(null);
    });
    return () => {
      active = false;
    };
  }, [battle.slug, battle.status, authenticated, loadState]);

  useEffect(() => {
    if (state?.battleStatus !== 'active' || !state.votingEndsAt) return;
    const countdown = getBattleCountdown(state.votingEndsAt, now);
    if (!countdown) return;
    if (countdown.expired) {
      void loadState();
      return;
    }
    const timer = window.setTimeout(() => setNow(Date.now()), countdown.refreshEveryMs ?? 60_000);
    return () => window.clearTimeout(timer);
  }, [loadState, now, state?.battleStatus, state?.votingEndsAt]);

  async function vote(side: 'challenger' | 'opponent') {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/battles/${battle.slug}/votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ side }),
      });
      const body = (await response.json()) as VoteState & { detail?: string };
      if (!response.ok) throw new Error(body.detail ?? 'No fue posible registrar el voto.');
      setState(body);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No fue posible registrar el voto.');
    } finally {
      setBusy(false);
    }
  }

  if (!state) {
    return <p className="mt-5 text-xs text-zinc-600">Preparando votación…</p>;
  }

  const challenger = battle.entries.find((entry) => entry.side === 'challenger');
  const opponent = battle.entries.find((entry) => entry.side === 'opponent');
  if (!challenger || !opponent) return null;
  const countdown = getBattleCountdown(state.votingEndsAt, now);
  const exactDeadline = formatExactDeadline(state.votingEndsAt);

  return (
    <div className="vote-panel mt-5 rounded-2xl p-4 sm:p-5">
      <SponsoredSlot slot="battle_voting" compact />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[0.62rem] font-black uppercase tracking-[0.18em] text-fuchsia-200">
            Votación de la audiencia
          </p>
        </div>
      </div>

      {state.battleStatus === 'closed' ? (
        <div className="mt-4 rounded-xl border border-emerald-300/15 bg-emerald-300/[0.05] px-4 py-3">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-200">
            Resultado final
          </p>
        </div>
      ) : countdown ? (
        <div className={`countdown-box countdown-${countdown.tone} mt-4 rounded-xl px-4 py-4`}>
          <p className="text-[0.62rem] font-black uppercase tracking-[0.18em]">
            La votación finaliza en
          </p>
          <p className="mt-1 text-xl font-black tracking-tight sm:text-2xl">{countdown.label}</p>
          {exactDeadline && !countdown.expired && (
            <p className="mt-2 text-xs opacity-70">Cierre: {exactDeadline}</p>
          )}
        </div>
      ) : null}

      {state.canVote && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <VoteButton
            artist={challenger.artistName}
            disabled={busy}
            onClick={() => vote('challenger')}
          />
          <VoteButton
            artist={opponent.artistName}
            disabled={busy}
            onClick={() => vote('opponent')}
          />
        </div>
      )}

      {!state.canVote && !state.results && (
        <p className="mt-4 text-sm leading-6 text-zinc-400">
          {reasonMessage(state.reason, authenticated)}
        </p>
      )}

      {state.results && (
        <div className="mt-5 space-y-4">
          <ResultBar
            artist={challenger.artistName}
            votes={state.results.challenger}
            percent={state.results.challengerPercent}
            selected={state.myVote === 'challenger'}
            winner={state.winnerSide === 'challenger'}
          />
          <ResultBar
            artist={opponent.artistName}
            votes={state.results.opponent}
            percent={state.results.opponentPercent}
            selected={state.myVote === 'opponent'}
            winner={state.winnerSide === 'opponent'}
          />
          <p className="text-center text-[0.65rem] uppercase tracking-[0.16em] text-zinc-600">
            {state.results.total}{' '}
            {state.results.total === 1 ? 'voto registrado' : 'votos registrados'}
            {state.winnerSide === 'tie' ? ' · Empate' : ''}
          </p>
        </div>
      )}

      {error && <p className="mt-4 text-sm text-rose-300">{error}</p>}
    </div>
  );
}

function VoteButton({
  artist,
  disabled,
  onClick,
}: {
  artist: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-xl border border-fuchsia-300/20 bg-fuchsia-300/[0.07] px-3 py-3 text-sm font-bold text-white transition hover:border-fuchsia-200/50 hover:bg-fuchsia-300/15 disabled:opacity-50"
    >
      Votar por {artist}
    </button>
  );
}

function ResultBar({
  artist,
  votes,
  percent,
  selected,
  winner,
}: {
  artist: string;
  votes: number;
  percent: number;
  selected: boolean;
  winner: boolean;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3 text-xs">
        <span className="font-bold text-zinc-200">
          {artist} {selected ? '· Tu voto' : ''} {winner ? '· Ganador' : ''}
        </span>
        <span className="text-zinc-500">
          {percent}% · {votes}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-400 transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function reasonMessage(reason: VoteState['reason'], authenticated: boolean) {
  if (!authenticated || reason === 'authentication_required') {
    return 'Inicia sesión y completa tu perfil para votar.';
  }
  if (reason === 'profile_required') return 'Completa tu perfil antes de votar.';
  if (reason === 'participant_cannot_vote') {
    return 'Los contrincantes no pueden votar en su propia batalla.';
  }
  if (reason === 'voting_closed') return 'La votación terminó.';
  if (reason === 'already_voted') return 'Tu voto ya fue registrado.';
  return 'La votación todavía no está disponible.';
}
